import styles from './edit-license-plate-info-modal.module.sass';
import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLicensePlateUpdate } from '../use-license-plate-update';
import analytics from 'service/analytics';
import EditLicensePlate from 'modules/edit-license-plate';
import Button from 'componets/button';
import LightBoxLayout from 'componets/light-box-layout';
import { InfoCircleFillIcon } from 'icons';

export const EditLicensePlateInfoModal = ({
  isOpen,
  onClose,
  licensePlateId,
  plateNumber,
  defaultEnergySource,
  defaultVehicleType,
  defaultOilType,
}) => {
  const queryClient = useQueryClient();

  const [isLicensePlateUpdateLoading, setIsLicensePlateUpdateLoading] = useState(false);

  const [plateNumberError, setPlateNumberError] = useState('');
  const [energySource, setEnergySource] = useState(defaultEnergySource ?? 'fuel');
  const [vehicleType, setVehicleType] = useState(defaultVehicleType ?? 'car');
  const [oilType, setOilType] = useState(defaultOilType ?? 'gas_92');

  const { updateLicensePlateMutateAsync } = useLicensePlateUpdate();

  const handleEnergySourceChange = useCallback(source => {
    if (source === 'fuel') {
      analytics.track('車牌頁_新增車牌_油車_click');
    } else {
      analytics.track('車牌頁_新增車牌_電動車_click');
    }

    setEnergySource(source);
  }, []);

  const handleVehicleTypeChange = useCallback(
    type => {
      if (energySource === 'fuel') {
        switch (type) {
          case 'car':
            analytics.track('車牌頁_修改車牌_汽車_click');
            break;
          case 'moto':
            analytics.track('車牌頁_修改車牌_機車_click');
            break;
          case 'trailer':
            analytics.track('車牌頁_修改車牌_拖車_click');
            break;
          default:
            break;
        }
      } else {
        switch (type) {
          case 'car':
            analytics.track('車牌頁_修改車牌_電動汽車_click');
            break;
          case 'moto':
            analytics.track('車牌頁_修改車牌_電動機車_click');
            break;
          default:
            break;
        }
      }
      setVehicleType(type);
    },
    [energySource]
  );

  const handleOilTypeChange = useCallback(type => {
    switch (type) {
      case 'gas_92':
        analytics.track('車牌頁_修改車牌_92汽油_click');
        break;
      case 'gas_95':
        analytics.track('車牌頁_修改車牌_95汽油_click');
        break;
      case 'gas_98':
        analytics.track('車牌頁_修改車牌_98汽油_click');
        break;
      case 'diesel':
        analytics.track('車牌頁_修改車牌_柴油_click');
        break;
      default:
        break;
    }

    setOilType(type);
  }, []);

  const handlePlateNumberFocus = useCallback(() => analytics.track('車牌頁_修改車牌_輸入車牌_click'), []);

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault();

      setIsLicensePlateUpdateLoading(true);

      analytics.licensePlatesEditPlateInfoConfirmButtonClick();

      try {
        await updateLicensePlateMutateAsync({
          id: licensePlateId,
          plateNumber,
          energySource,
          vehicleType,
          oilType,
        });

        await queryClient.invalidateQueries('license-plates');
      } catch (error) {
        const { message } = error?.response.data.error ?? {};
        setPlateNumberError(message ?? '系統錯誤，請稍後再試');
        return;
      } finally {
        setIsLicensePlateUpdateLoading(false);
      }

      onClose();
    },
    [
      licensePlateId,
      plateNumber,
      energySource,
      vehicleType,
      oilType,
      queryClient,
      onClose,
      updateLicensePlateMutateAsync,
    ]
  );

  return (
    <LightBoxLayout show={isOpen}>
      <form className={styles['edit-license-plate-form']} onSubmit={handleSubmit}>
        <EditLicensePlate
          isEdit
          title={`修改 ${plateNumber} 車牌資料`}
          plate={plateNumber}
          plateErrorMessage={plateNumberError}
          onPlateFocus={handlePlateNumberFocus}
          energySource={energySource}
          onEnergySourceSelect={handleEnergySourceChange}
          vehicleType={vehicleType}
          onVehicleTypeSelect={handleVehicleTypeChange}
          oilType={oilType}
          onOilTypeSelect={handleOilTypeChange}
        />
        <div className={styles['hint']}>
          <InfoCircleFillIcon width={20} height={20} style={{ marginRight: '4px' }} />
          送出儲存後將在 2 日內完成修改，完成修改前使用加油仍然會扣款，車牌需等待重新開通服務。
        </div>
        <div className={styles['button-group']}>
          <Button type="button" variant="text" onClick={onClose}>
            取消
          </Button>
          <Button loading={isLicensePlateUpdateLoading} type="submit">
            儲存
          </Button>
        </div>
      </form>
    </LightBoxLayout>
  );
};
